import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { Button } from 'primeng/button';
import { DatePipe } from '@angular/common';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { TooltipModule } from 'primeng/tooltip';
import { AnnouncementsApi } from '../../../../../shared/services/api/announcements.api';
import { AnnouncementReadStatus } from '../../../../../shared/models/announcement-read-status';
import { AnnouncementChannel } from '../../../../../shared/enums/announcement-channel.enum';
import { catchError, EMPTY, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpResponseService } from '../../../../../core/services/http-error-response.service';

@Component({
  selector: 'app-announcement-read-status-dialog',
  imports: [Button, DatePipe, TooltipModule],
  templateUrl: './announcement-read-status-dialog.html',
  styleUrl: './announcement-read-status-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AnnouncementsApi],
})
export class AnnouncementReadStatusDialog implements OnInit {
  announcementId = input.required<number>();
  announcementChannel = input<AnnouncementChannel | null>(null);

  protected readStatus = signal<AnnouncementReadStatus | null>(null);
  protected readonly AnnouncementChannel = AnnouncementChannel;
  protected readonly dialogRef = inject(DynamicDialogRef);
  readonly #announcementsApi: AnnouncementsApi = inject(AnnouncementsApi);
  readonly #destroyRef = inject(DestroyRef);
  readonly #httpResponseService = inject(HttpResponseService);

  protected get emailStatsLabel(): string {
    if (!this.readStatus()) {
      return '-';
    }

    const sent = this.readStatus()?.emailsSent ?? 0;
    const planned = this.readStatus()?.emailsPlanned ?? 0;

    return `${sent} (${planned})`;
  }

  protected readonly shouldShowEmailStats = computed(
    () =>
      this.announcementChannel() === AnnouncementChannel.EMAIL ||
      this.announcementChannel() === AnnouncementChannel.SYSTEM_I_EMAIL,
  );

  protected readonly shouldShowSystemStats = computed(
    () =>
      this.announcementChannel() === AnnouncementChannel.SYSTEM ||
      this.announcementChannel() === AnnouncementChannel.SYSTEM_I_EMAIL,
  );

  ngOnInit(): void {
    this.#getAnnouncementReadStatus();
  }

  #getAnnouncementReadStatus(): void {
    this.#announcementsApi
      .getAnnouncementReadStatus$(this.announcementId())
      .pipe(
        tap((readStatus) => this.readStatus.set(readStatus)),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas pobierania statystyk.',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected close(): void {
    this.dialogRef.close();
  }
}
